import type { WeaponKind } from './WeaponSystem.js';
import { getRarityCssColor } from './materials.js';
import type { WeaponRarity } from './FpsWeaponRarity.js';

const WEAPON_SLOT_COUNT = 3;

type Slot0Kind = 'ar' | 'smg' | 'pistol';
type HotbarIconKind = Slot0Kind | 'shotgun' | 'sniper' | 'build' | 'med';

const SVG_NS = 'http://www.w3.org/2000/svg';

function weaponIconSvg(kind: HotbarIconKind): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 32 32');
  svg.setAttribute('width', '28');
  svg.setAttribute('height', '28');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText = 'display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.55))';

  const stroke = 'rgba(255,255,255,0.88)';
  const fill = 'rgba(200,220,255,0.35)';

  const add = (el: SVGElement): void => {
    svg.appendChild(el);
  };

  const rect = (x: number, y: number, w: number, h: number, r = 1.5): SVGRectElement => {
    const el = document.createElementNS(SVG_NS, 'rect');
    el.setAttribute('x', String(x));
    el.setAttribute('y', String(y));
    el.setAttribute('width', String(w));
    el.setAttribute('height', String(h));
    el.setAttribute('rx', String(r));
    el.setAttribute('fill', fill);
    el.setAttribute('stroke', stroke);
    el.setAttribute('stroke-width', '1.2');
    return el;
  };

  const line = (x1: number, y1: number, x2: number, y2: number, sw = 1.4): SVGLineElement => {
    const el = document.createElementNS(SVG_NS, 'line');
    el.setAttribute('x1', String(x1));
    el.setAttribute('y1', String(y1));
    el.setAttribute('x2', String(x2));
    el.setAttribute('y2', String(y2));
    el.setAttribute('stroke', stroke);
    el.setAttribute('stroke-width', String(sw));
    el.setAttribute('stroke-linecap', 'round');
    return el;
  };

  switch (kind) {
    case 'ar':
      add(rect(6, 12, 16, 6, 2));
      add(rect(20, 10, 8, 4, 1));
      add(rect(8, 18, 4, 8, 1));
      add(rect(14, 18, 3, 6, 1));
      break;
    case 'smg':
      add(rect(8, 13, 12, 5, 1.5));
      add(rect(18, 11, 7, 3, 1));
      add(rect(10, 18, 3, 7, 1));
      add(line(12, 15, 16, 15, 1));
      break;
    case 'pistol':
      add(rect(10, 12, 10, 6, 1.5));
      add(rect(18, 13, 5, 3, 1));
      add(rect(12, 18, 4, 7, 1));
      break;
    case 'shotgun':
      add(rect(4, 13, 18, 5, 1.5));
      add(rect(20, 12, 7, 4, 1));
      add(line(6, 16, 20, 16, 1.2));
      add(rect(9, 18, 4, 7, 1));
      break;
    case 'sniper':
      add(rect(3, 13, 20, 4, 1));
      add(rect(21, 11, 8, 5, 1));
      add(rect(10, 9, 5, 4, 1));
      add(rect(8, 17, 3, 8, 1));
      add(line(14, 11, 18, 11, 1));
      break;
    case 'build':
      add(rect(7, 8, 10, 10, 1));
      add(rect(15, 12, 10, 10, 1));
      add(line(7, 8, 15, 12, 1.2));
      add(line(17, 8, 25, 12, 1.2));
      add(line(7, 18, 15, 22, 1.2));
      break;
    case 'med':
      add(rect(8, 8, 16, 16, 3));
      add(rect(14, 11, 4, 10, 1));
      add(rect(11, 14, 10, 4, 1));
      break;
    default: {
      const neverKind: never = kind;
      void neverKind;
    }
  }

  return svg;
}

function slot0IconKind(kind: Slot0Kind): HotbarIconKind {
  return kind;
}

/** Bottom-center Fortnite-style weapon bar (5 slots) + consumable counts (6/7). */
export class FpsHotbar {
  private readonly root: HTMLDivElement;
  private readonly slots: HTMLDivElement[] = [];
  private readonly slotLabels: HTMLSpanElement[] = [];
  private readonly slotIcons: SVGSVGElement[] = [];
  private readonly consumablesRow!: HTMLDivElement;
  private readonly miniCountEl!: HTMLSpanElement;
  private readonly bandageCountEl!: HTMLSpanElement;
  private index = 0;
  private buildDisabled = false;
  private slot0Kind: 'ar' | 'smg' | 'pistol' = 'ar';
  private readonly slotRarities: WeaponRarity[] = ['common', 'common', 'common'];
  /** Tracks whether a looted/vended weapon has been placed in each gun slot. */
  private readonly slotFilled: boolean[] = [false, false, false];
  private readonly modChip: HTMLDivElement;
  private modChipLetter = '';
  private readonly animStyle: HTMLStyleElement;
  private readonly dbnoHud: HTMLDivElement;
  private readonly dbnoRingFill: SVGCircleElement;
  private readonly dbnoSeconds: HTMLSpanElement;
  private static readonly DBNO_RING_R = 44;
  private static readonly DBNO_RING_C = 2 * Math.PI * FpsHotbar.DBNO_RING_R;
  private slotAnimTimer = 0;
  private disposed = false;
  private readonly heatBars: HTMLDivElement[] = [];
  private readonly heatFills: HTMLDivElement[] = [];
  private readonly reloadRings: SVGCircleElement[] = [];
  private static readonly RELOAD_RING_R = 28;
  private static readonly RELOAD_RING_C = 2 * Math.PI * FpsHotbar.RELOAD_RING_R;

  constructor(mount: HTMLElement) {
    this.animStyle = document.createElement('style');
    this.animStyle.textContent = `@keyframes fpsHotbarSlotPop {
  0% { transform: scale(1); }
  38% { transform: scale(1.08); }
  100% { transform: scale(1); }
}
@keyframes fpsHotbarGlowFlash {
  0% { filter: brightness(1); }
  35% { filter: brightness(1.35); }
  100% { filter: brightness(1); }
}
.fps-hotbar-slot-pop { animation: fpsHotbarSlotPop 0.28s cubic-bezier(0.22, 1.15, 0.36, 1); }
.fps-hotbar-slot-glow { animation: fpsHotbarGlowFlash 0.38s ease-out; }
.fps-hotbar-root{bottom:calc(env(safe-area-inset-bottom,0px) + 18px)!important}
@media (max-width:720px){
  .fps-hotbar-root{bottom:calc(env(safe-area-inset-bottom,0px) + 8px)!important;gap:4px!important;max-width:calc(100vw - env(safe-area-inset-left,0px) - env(safe-area-inset-right,0px) - 12px)!important;justify-content:center!important}
  .fps-hotbar-slot{width:clamp(40px,11.5vw,50px)!important;height:clamp(40px,11.5vw,50px)!important;font-size:10px!important}
  .fps-hotbar-consumables{gap:4px!important;margin-left:0!important}
  .fps-hotbar-consumable{width:clamp(32px,9vw,38px)!important;height:clamp(32px,9vw,38px)!important;font-size:8px!important}
  .fps-hotbar-mod-chip{bottom:calc(env(safe-area-inset-bottom,0px) + 56px)!important;width:22px!important;height:22px!important;min-width:22px!important;font-size:10px!important}
}
@media (max-width:520px),(max-height:520px){
  .fps-hotbar-root{gap:4px!important;bottom:calc(env(safe-area-inset-bottom,0px) + 6px)!important}
  .fps-hotbar-slot{width:38px!important;height:38px!important;border-width:1px!important;font-size:9px!important}
  .fps-hotbar-slot span:first-child{font-size:8px!important;top:2px!important;left:4px!important}
  .fps-hotbar-consumables{display:flex!important;gap:3px!important}
  .fps-hotbar-consumable{width:30px!important;height:30px!important;border-width:1px!important;font-size:7px!important}
  .fps-hotbar-consumable span:first-child{display:none!important}
  .fps-hotbar-mod-chip{bottom:calc(env(safe-area-inset-bottom,0px) + 48px)!important}
}
@media (max-height:420px){.fps-hotbar-root{transform:translateX(-50%) scale(.88)!important;transform-origin:bottom center!important}}
.fps-hotbar-reload-ring{position:absolute;inset:0;pointer-events:none;display:none}
.fps-hotbar-reload-track{stroke:rgba(255,255,255,0.12)}
.fps-hotbar-reload-fill{stroke:#2ee6ff;stroke-linecap:round;transition:stroke-dashoffset 0.08s linear}
@media (prefers-reduced-motion:reduce){.fps-hotbar-reload-fill{transition:none!important}}
body[data-fps-reduced-motion="true"] .fps-hotbar-reload-fill{transition:none!important}
body[data-fps-reduced-motion="true"] .fps-hotbar-slot-pop,body[data-fps-reduced-motion="true"] .fps-hotbar-slot-glow{animation:none!important}
.fps-dbno-ring{position:fixed;left:50%;bottom:calc(env(safe-area-inset-bottom,0px) + 96px);transform:translateX(-50%);z-index:36;display:none;align-items:center;justify-content:center;pointer-events:none}
.fps-dbno-ring svg{filter:drop-shadow(0 0 14px rgba(255,70,70,0.45))}
.fps-dbno-ring-track{stroke:rgba(255,255,255,0.14)}
.fps-dbno-ring-fill{stroke:#ff4a4a;stroke-linecap:round;transition:stroke-dashoffset 0.12s linear}
.fps-dbno-ring-label{position:absolute;font:900 18px system-ui,sans-serif;color:#ffb0b0;text-shadow:0 0 10px rgba(255,60,60,0.55)}
@media (max-width:720px){.fps-dbno-ring{bottom:calc(env(safe-area-inset-bottom,0px) + 72px)!important}.fps-dbno-ring-label{font-size:15px!important}}
`;
    document.head.appendChild(this.animStyle);
    this.root = document.createElement('div');
    this.root.className = 'fps-hotbar-root';
    this.root.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:18px',
      'transform:translateX(-50%)',
      'display:flex',
      'gap:8px',
      'z-index:35',
      'pointer-events:none',
    ].join(';');

    const defaultIcons: HotbarIconKind[] = ['ar', 'shotgun', 'sniper', 'build', 'med'];
    for (let i = 0; i < 5; i++) {
      const s = document.createElement('div');
      s.className = 'fps-hotbar-slot';
      s.style.cssText = [
        'width:64px',
        'height:64px',
        'border-radius:var(--fps-radius-sm,6px)',
        'background:linear-gradient(135deg, rgba(20,24,38,0.95) 0%, rgba(12,14,24,0.98) 100%)',
        'border:2px solid rgba(80,90,120,0.85)',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'font-family:var(--fps-font,system-ui,sans-serif)',
        'color:rgba(255,255,255,0.75)',
        'box-shadow:inset 0 2px 4px rgba(255,255,255,0.1), 0 2px 8px rgba(0,0,0,0.6)',
        'transform-origin:bottom center',
        'transition:all 0.15s cubic-bezier(0.2, 0.9, 0.3, 1.2)',
      ].join(';');
      const key = document.createElement('span');
      key.textContent = String(i + 1);
      key.style.cssText =
        'position:absolute;top:4px;left:6px;font:700 10px var(--fps-font,system-ui);opacity:0.6;color:#aaccff';
      const lab = document.createElement('span');
      lab.style.cssText = 'display:none';
      const icon = weaponIconSvg(defaultIcons[i] ?? 'ar');
      s.style.position = 'relative';
      s.appendChild(key);
      s.appendChild(lab);
      s.appendChild(icon);
      if (i < 3) {
        const heatBar = document.createElement('div');
        heatBar.style.cssText = [
          'position:absolute',
          'left:6px',
          'right:6px',
          'bottom:5px',
          'height:4px',
          'border-radius:2px',
          'background:rgba(0,0,0,0.45)',
          'overflow:hidden',
          'display:none',
        ].join(';');
        const heatFill = document.createElement('div');
        heatFill.style.cssText = [
          'height:100%',
          'width:0%',
          'border-radius:2px',
          'background:linear-gradient(90deg,#ffb347,#ff4a1a)',
          'transition:width 0.08s linear, background 0.12s ease',
        ].join(';');
        heatBar.appendChild(heatFill);
        s.appendChild(heatBar);
        this.heatBars.push(heatBar);
        this.heatFills.push(heatFill);

        const reloadRing = document.createElement('div');
        reloadRing.className = 'fps-hotbar-reload-ring';
        const svgNs = 'http://www.w3.org/2000/svg';
        const reloadSvg = document.createElementNS(svgNs, 'svg');
        reloadSvg.setAttribute('width', '64');
        reloadSvg.setAttribute('height', '64');
        reloadSvg.setAttribute('viewBox', '0 0 64 64');
        const reloadTrack = document.createElementNS(svgNs, 'circle');
        reloadTrack.setAttribute('cx', '32');
        reloadTrack.setAttribute('cy', '32');
        reloadTrack.setAttribute('r', String(FpsHotbar.RELOAD_RING_R));
        reloadTrack.setAttribute('fill', 'none');
        reloadTrack.setAttribute('stroke-width', '3');
        reloadTrack.classList.add('fps-hotbar-reload-track');
        const reloadFill = document.createElementNS(svgNs, 'circle');
        reloadFill.setAttribute('cx', '32');
        reloadFill.setAttribute('cy', '32');
        reloadFill.setAttribute('r', String(FpsHotbar.RELOAD_RING_R));
        reloadFill.setAttribute('fill', 'none');
        reloadFill.setAttribute('stroke-width', '3');
        reloadFill.classList.add('fps-hotbar-reload-fill');
        reloadFill.style.transform = 'rotate(-90deg)';
        reloadFill.style.transformOrigin = '32px 32px';
        reloadFill.style.strokeDasharray = String(FpsHotbar.RELOAD_RING_C);
        reloadFill.style.strokeDashoffset = String(FpsHotbar.RELOAD_RING_C);
        reloadSvg.appendChild(reloadTrack);
        reloadSvg.appendChild(reloadFill);
        reloadRing.appendChild(reloadSvg);
        s.appendChild(reloadRing);
        this.reloadRings.push(reloadFill);
      }
      this.root.appendChild(s);
      this.slots.push(s);
      this.slotLabels.push(lab);
      this.slotIcons.push(icon);
    }

    this.consumablesRow = document.createElement('div');
    this.consumablesRow.className = 'fps-hotbar-consumables';
    this.consumablesRow.style.cssText = [
      'display:flex',
      'gap:6px',
      'margin-left:4px',
    ].join(';');

    const mkConsumable = (key: string, label: string, border: string): HTMLSpanElement => {
      const wrap = document.createElement('div');
      wrap.className = 'fps-hotbar-consumable';
      wrap.style.cssText = [
        'position:relative',
        'width:44px',
        'height:44px',
        'border-radius:9px',
        'background:rgba(14,18,32,0.88)',
        `border:2px solid ${border}`,
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'font:700 10px system-ui,sans-serif',
        'color:rgba(255,255,255,0.82)',
        'box-shadow:inset 0 1px 0 rgba(255,255,255,0.06)',
      ].join(';');
      const keyEl = document.createElement('span');
      keyEl.textContent = key;
      keyEl.style.cssText =
        'position:absolute;top:3px;left:5px;font-size:9px;opacity:0.45;color:#8ab';
      const labEl = document.createElement('span');
      labEl.textContent = label;
      const countEl = document.createElement('span');
      countEl.textContent = '0';
      countEl.style.cssText =
        'position:absolute;bottom:3px;right:5px;font-size:11px;font-weight:800;color:#fff;text-shadow:0 1px 3px #000';
      wrap.appendChild(keyEl);
      wrap.appendChild(labEl);
      wrap.appendChild(countEl);
      this.consumablesRow.appendChild(wrap);
      return countEl;
    };

    this.miniCountEl = mkConsumable('6', 'Mini', 'rgba(52,152,219,0.85)');
    this.bandageCountEl = mkConsumable('7', 'Band', 'rgba(88,214,141,0.85)');

    this.root.appendChild(this.consumablesRow);
    mount.appendChild(this.root);

    this.modChip = document.createElement('div');
    this.modChip.className = 'fps-hotbar-mod-chip';
    this.modChip.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:78px',
      'transform:translateX(-50%)',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'width:28px',
      'height:28px',
      'border-radius:8px',
      'background:rgba(18,12,32,0.92)',
      'border:2px solid #b44dff',
      'font:800 13px system-ui,sans-serif',
      'color:#e8c8ff',
      'letter-spacing:0.04em',
      'box-shadow:0 0 12px rgba(180,77,255,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
      'z-index:35',
      'pointer-events:none',
    ].join(';');
    mount.appendChild(this.modChip);

    this.dbnoHud = document.createElement('div');
    this.dbnoHud.className = 'fps-dbno-ring';
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('width', '104');
    svg.setAttribute('height', '104');
    svg.setAttribute('viewBox', '0 0 104 104');
    const track = document.createElementNS(svgNs, 'circle');
    track.setAttribute('cx', '52');
    track.setAttribute('cy', '52');
    track.setAttribute('r', String(FpsHotbar.DBNO_RING_R));
    track.setAttribute('fill', 'none');
    track.setAttribute('stroke-width', '6');
    track.classList.add('fps-dbno-ring-track');
    this.dbnoRingFill = document.createElementNS(svgNs, 'circle');
    this.dbnoRingFill.setAttribute('cx', '52');
    this.dbnoRingFill.setAttribute('cy', '52');
    this.dbnoRingFill.setAttribute('r', String(FpsHotbar.DBNO_RING_R));
    this.dbnoRingFill.setAttribute('fill', 'none');
    this.dbnoRingFill.setAttribute('stroke-width', '6');
    this.dbnoRingFill.classList.add('fps-dbno-ring-fill');
    this.dbnoRingFill.style.transform = 'rotate(-90deg)';
    this.dbnoRingFill.style.transformOrigin = '52px 52px';
    this.dbnoRingFill.style.strokeDasharray = String(FpsHotbar.DBNO_RING_C);
    this.dbnoRingFill.style.strokeDashoffset = '0';
    svg.appendChild(track);
    svg.appendChild(this.dbnoRingFill);
    this.dbnoHud.appendChild(svg);
    this.dbnoSeconds = document.createElement('span');
    this.dbnoSeconds.className = 'fps-dbno-ring-label';
    this.dbnoSeconds.textContent = '0';
    this.dbnoHud.appendChild(this.dbnoSeconds);
    mount.appendChild(this.dbnoHud);

    this.setSelected(0);
  }

  /** Circular bleed-out timer for duo/squad DBNO (ratio 1 = full time left). */
  setDbnoBleedout(active: boolean, ratio: number, secondsLeft: number): void {
    if (!active) {
      this.dbnoHud.style.display = 'none';
      return;
    }
    this.dbnoHud.style.display = 'flex';
    const clamped = Math.max(0, Math.min(1, ratio));
    this.dbnoRingFill.style.strokeDashoffset = String(FpsHotbar.DBNO_RING_C * (1 - clamped));
    this.dbnoSeconds.textContent = String(Math.max(0, Math.ceil(secondsLeft)));
  }

  /** Purple mod chip above hotbar — up to two letters e.g. "S M", or hidden when no mods. */
  setModChip(letter: string | null): void {
    if (!letter) {
      this.modChip.style.display = 'none';
      this.modChipLetter = '';
      this.modChip.style.minWidth = '28px';
      return;
    }
    this.modChipLetter = letter;
    this.modChip.textContent = letter;
    this.modChip.style.minWidth = letter.length > 2 ? '44px' : '28px';
    this.modChip.style.display = 'flex';
  }

  getModChipLetter(): string {
    return this.modChipLetter;
  }

  setConsumableCounts(mini: number, bandage: number): void {
    this.miniCountEl.textContent = String(Math.max(0, mini));
    this.bandageCountEl.textContent = String(Math.max(0, bandage));
    this.miniCountEl.style.opacity = mini > 0 ? '1' : '0.35';
    this.bandageCountEl.style.opacity = bandage > 0 ? '1' : '0.35';
  }

  setBuildDisabled(disabled: boolean): void {
    this.buildDisabled = disabled;
    this.applyWeaponRarityBorders();
  }

  isBuildDisabled(): boolean {
    return this.buildDisabled;
  }

  setSelected(i: number): void {
    const next = Math.max(0, Math.min(4, i));
    const changed = next !== this.index;
    this.index = next;
    this.applyWeaponRarityBorders();
    if (changed) this.playSlotSelectAnim(next);
  }

  private playSlotSelectAnim(slotIndex: number): void {
    // Phase 34X-23: stronger select bounce on active hotbar slot
    const slot = this.slots[slotIndex];
    if (!slot) return;
    if (document.body.dataset.fpsReducedMotion === 'true' || (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false)) return;
    window.clearTimeout(this.slotAnimTimer);
    slot.classList.remove('fps-hotbar-slot-pop', 'fps-hotbar-slot-glow');
    void slot.offsetWidth;
    slot.classList.add('fps-hotbar-slot-pop', 'fps-hotbar-slot-glow');
    slot.style.transition = 'transform 0.18s cubic-bezier(0.22,1,0.36,1)';
    slot.style.transform = 'scale(1.12) translateY(-3px)';
    this.slotAnimTimer = window.setTimeout(() => {
      if (this.disposed) return;
      slot.style.transform = '';
      slot.classList.remove('fps-hotbar-slot-pop', 'fps-hotbar-slot-glow');
    }, 380);
  }

  getSelectedIndex(): number {
    return this.index;
  }

  /** Replace slot 0 icon with a dropped primary (AR-class) weapon. */
  setSlot0Weapon(kind: Slot0Kind): void {
    this.slot0Kind = kind;
    this.setSlotIcon(0, slot0IconKind(kind));
    this.applyWeaponRarityBorders();
  }

  getSlot0Weapon(): 'ar' | 'smg' | 'pistol' {
    return this.slot0Kind;
  }

  setSlotRarity(slotIndex: number, rarity: WeaponRarity): void {
    if (slotIndex < 0 || slotIndex > 2) return;
    this.slotRarities[slotIndex] = rarity;
    this.applyWeaponRarityBorders();
  }

  getSlotRarity(slotIndex: number): WeaponRarity {
    return this.slotRarities[Math.max(0, Math.min(2, slotIndex))] ?? 'common';
  }

  isSlotFilled(slotIndex: number): boolean {
    if (slotIndex < 0 || slotIndex >= WEAPON_SLOT_COUNT) return true;
    return this.slotFilled[slotIndex] ?? false;
  }

  markSlotFilled(slotIndex: number): void {
    if (slotIndex < 0 || slotIndex >= WEAPON_SLOT_COUNT) return;
    this.slotFilled[slotIndex] = true;
  }

  findFirstEmptyWeaponSlot(): number | null {
    for (let i = 0; i < WEAPON_SLOT_COUNT; i++) {
      if (!this.slotFilled[i]) return i;
    }
    return null;
  }

  areAllWeaponSlotsFilled(): boolean {
    return this.slotFilled.every(Boolean);
  }

  resetWeaponSlots(defaultSlot0: Slot0Kind = 'ar'): void {
    for (let i = 0; i < WEAPON_SLOT_COUNT; i++) {
      this.slotFilled[i] = false;
      this.slotRarities[i] = 'common';
    }
    this.slot0Kind = defaultSlot0;
    this.setSlotIcon(0, slot0IconKind(defaultSlot0));
    this.setSlotIcon(1, 'shotgun');
    this.setSlotIcon(2, 'sniper');
    this.applyWeaponRarityBorders();
  }

  private setSlotIcon(slotIndex: number, kind: HotbarIconKind): void {
    const slot = this.slots[slotIndex];
    const prev = this.slotIcons[slotIndex];
    if (!slot || !prev) return;
    const next = weaponIconSvg(kind);
    prev.replaceWith(next);
    this.slotIcons[slotIndex] = next;
  }

  getBestSlotForWeapon(kind: WeaponKind): number {
    if (kind === 'shotgun') return 1;
    if (kind === 'sniper') return 2;
    return 0;
  }

  /** Map slot index to weapon kind for slots 0–2; 3–4 are not guns. */
  weaponKindForSlot(slot: number): WeaponKind | null {
    if (slot === 0) return this.slot0Kind;
    if (slot === 1) return 'shotgun';
    if (slot === 2) return 'sniper';
    return null;
  }

  private selectedSlotStyle(): { border: string; boxShadow: string; transform: string } {
    return {
      border: '2px solid transparent',
      boxShadow:
        '0 0 20px rgba(80,240,255,0.65), 0 0 10px rgba(255,255,255,0.55), inset 0 0 0 3px rgba(46,230,255,1), inset 0 2px 4px rgba(255,255,255,0.2)',
      transform: 'translateY(-6px) scale(1.04)',
    };
  }

  setColorblindMode(_on: boolean): void {
    this.applyWeaponRarityBorders();
  }

  /** Overheat bar on weapon slots 0–2; ratio 0 hides the bar. */
  setWeaponHeat(slotIndex: number, ratio: number, overheated = false): void {
    if (slotIndex < 0 || slotIndex > 2) return;
    const bar = this.heatBars[slotIndex];
    const fill = this.heatFills[slotIndex];
    if (!bar || !fill) return;
    const heat = Math.max(0, Math.min(1, ratio));
    if (heat <= 0.01 && !overheated) {
      bar.style.display = 'none';
      return;
    }
    bar.style.display = 'block';
    fill.style.width = `${Math.round(heat * 100)}%`;
    fill.style.background = overheated
      ? 'linear-gradient(90deg,#ff3030,#ff7070)'
      : heat > 0.82
        ? 'linear-gradient(90deg,#ff8a1a,#ff3030)'
        : 'linear-gradient(90deg,#ffd34d,#ff6a1a)';
  }

  /** Circular reload progress on weapon slots 0–2; only shown on the active slot. */
  setReloadProgress(slotIndex: number, ratio: number, active = false): void {
    for (let i = 0; i < 3; i++) {
      const ring = this.reloadRings[i];
      const wrap = ring?.parentElement?.parentElement as HTMLDivElement | undefined;
      if (!ring || !wrap) continue;
      const show = i === slotIndex && active && ratio > 0.02;
      wrap.style.display = show ? 'block' : 'none';
      if (!show) continue;
      const clamped = Math.max(0, Math.min(1, ratio));
      ring.style.strokeDashoffset = String(FpsHotbar.RELOAD_RING_C * (1 - clamped));
    }
  }

  /** Fortnite-style weapon slot borders tinted by rarity (slots 0–2). */
  applyWeaponRarityBorders(): void {
    const selected = this.selectedSlotStyle();
    const borders = this.slotRarities.map((r) => `2px solid ${getRarityCssColor(r)}`);
    for (let i = 0; i < 3; i++) {
      const s = this.slots[i];
      if (!s) continue;
      const on = i === this.index;
      s.style.border = on ? selected.border : borders[i] ?? '2px solid rgba(80,90,120,0.85)';
      s.style.boxShadow = on ? selected.boxShadow : 'inset 0 2px 4px rgba(255,255,255,0.1), 0 2px 8px rgba(0,0,0,0.6)';
      s.style.transform = on ? selected.transform : 'translateY(0) scale(1)';
      s.style.background = on
        ? 'linear-gradient(135deg, rgba(28,36,58,0.98) 0%, rgba(18,24,42,0.98) 100%)'
        : 'linear-gradient(135deg, rgba(20,24,38,0.95) 0%, rgba(12,14,24,0.98) 100%)';
    }
    for (let k = 3; k < 5; k++) {
      const on = k === this.index;
      const buildOff = k === 3 && this.buildDisabled;
      this.slots[k]!.style.opacity = buildOff ? '0.38' : '';
      this.slots[k]!.style.filter = buildOff ? 'grayscale(1)' : '';
      if (k === 3) this.setSlotIcon(3, 'build');
      if (k === 4) this.setSlotIcon(4, 'med');
      const icon = this.slotIcons[k];
      if (icon) icon.style.opacity = buildOff ? '0.35' : '1';
      this.slots[k]!.style.border = on && !buildOff
        ? selected.border
        : buildOff
          ? '2px solid rgba(60,65,80,0.65)'
          : '2px solid rgba(80,90,120,0.85)';
      this.slots[k]!.style.boxShadow = on && !buildOff
        ? selected.boxShadow
        : 'inset 0 2px 4px rgba(255,255,255,0.1), 0 2px 8px rgba(0,0,0,0.6)';
      this.slots[k]!.style.transform = on && !buildOff ? selected.transform : 'translateY(0) scale(1)';
      this.slots[k]!.style.background = on && !buildOff
        ? 'linear-gradient(135deg, rgba(28,36,58,0.98) 0%, rgba(18,24,42,0.98) 100%)'
        : 'linear-gradient(135deg, rgba(20,24,38,0.95) 0%, rgba(12,14,24,0.98) 100%)';
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.clearTimeout(this.slotAnimTimer);
    this.animStyle.remove();
    this.dbnoHud.remove();
    this.modChip.remove();
    this.root.remove();
  }
}
